import { useEffect, useRef, useState } from 'react'
import { Search, X, Loader2 } from 'lucide-react'
import { api } from '../utils/api'
import { useAuthStore } from '../stores/authStore'

interface SearchResult {
  type: 'file' | 'folder'
  matchType: 'filename' | 'content' | 'foldername'
  relevance: number
  context?: string
  occurrences?: number
  item: any
}

interface Props {
  currentPath: string
  onResults: (results: SearchResult[], query: string) => void
  onClear: () => void
  onClose: () => void
}

// Compact search input designed to sit inline in the toolbar row alongside
// buttons. Height & rounding match the Upload / + New buttons so the row
// stays visually consistent. Filter UX is intentionally omitted — for
// power-user filter chips the sidebar view can host AdvancedSearch instead.
// Debounces at 300ms and clears results when query goes empty.
export function InlineSearchInput({ currentPath: _currentPath, onResults, onClear, onClose }: Props) {
  const { token, csrfToken } = useAuthStore()
  const [query, setQuery] = useState('')
  const [searching, setSearching] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => { inputRef.current?.focus() }, [])

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current)
    if (query.trim().length === 0) {
      // Empty query = leave search mode. Don't spam onClear on every keystroke —
      // debounce so onClear fires once when the user actually clears the box.
      timerRef.current = setTimeout(() => onClear(), 200)
      return
    }
    timerRef.current = setTimeout(async () => {
      setSearching(true)
      try {
        const res = await api.advancedSearch(
          { query, fileType: 'all', minSize: 0, maxSize: 0, dateFrom: '', dateTo: '', searchContent: false, isPublic: '' },
          token || '',
          csrfToken || ''
        )
        if (res.success && res.data) {
          onResults(res.data.results ?? [], query)
        } else {
          onResults([], query)
        }
      } catch {
        onResults([], query)
      } finally {
        setSearching(false)
      }
    }, 300)
    return () => { if (timerRef.current) clearTimeout(timerRef.current) }
    // onResults/onClear are recreated on each parent render but we only want to
    // trigger a search when the query changes — deliberately exclude them from
    // the dep array (their identity change should not fire a network call).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query])

  const handleClose = () => {
    setQuery('')
    onClear()
    onClose()
  }

  return (
    <div className="relative flex items-center">
      <Search className="pointer-events-none absolute left-3 h-4 w-4 text-dark-400" />
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={e => setQuery(e.target.value)}
        onKeyDown={e => { if (e.key === 'Escape') handleClose() }}
        placeholder="Search…"
        className="h-10 w-64 rounded-xl border border-white/10 bg-white/5 pl-9 pr-16 text-sm text-white placeholder-dark-500 outline-none transition focus:border-primary-500/50 focus:bg-white/10 sm:w-80"
      />
      <div className="absolute right-1.5 flex items-center gap-0.5">
        {searching && <Loader2 className="h-3.5 w-3.5 animate-spin text-dark-400" />}
        {/* Single X: closes the whole search when clicked. Clearing the query
            alone is handled by the debounced empty-query branch. */}
        <button
          onClick={handleClose}
          className="rounded-md p-1 text-dark-400 transition hover:bg-white/5 hover:text-white"
          title="Close search (Esc)"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}
